import { AlertTriangle, CheckCircle2, Clock, HelpCircle, Info, OctagonAlert, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

/**
 * Tones map to the reserved status palette. A tone is never shown by colour
 * alone: every badge carries a label, and status tones also carry an icon.
 */
export type Tone = 'neutral' | 'info' | 'ok' | 'pending' | 'warn' | 'serious' | 'crit';

export const TONE_CLASS: Record<Tone, string> = {
  neutral: 'border-transparent bg-muted text-muted-foreground',
  info: 'border-transparent bg-info-soft text-info',
  ok: 'border-transparent bg-success-soft text-success',
  // Blue like info, not the green brand accent, so "pending" never reads as "ok".
  pending: 'border-transparent bg-info-soft text-info',
  warn: 'border-transparent bg-warning-soft text-warning',
  serious: 'border-transparent bg-serious-soft text-serious',
  crit: 'border-transparent bg-critical-soft text-critical',
};

export const TONE_ICON: Partial<Record<Tone, LucideIcon>> = {
  ok: CheckCircle2,
  info: Info,
  pending: Clock,
  warn: AlertTriangle,
  serious: AlertTriangle,
  crit: OctagonAlert,
};

const BASE = 'inline-flex w-fit shrink-0 items-center gap-1 whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-semibold [&>svg]:size-3';

export function Badge({ tone = 'neutral', children, icon = true, title, className }: { tone?: Tone; children: ReactNode; icon?: boolean; title?: string; className?: string }) {
  const Icon = TONE_ICON[tone];
  return (
    <span className={cn(BASE, TONE_CLASS[tone], className)} title={title}>
      {icon && Icon ? <Icon aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

export function UnknownBadge({ children }: { children?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <span className={cn(BASE, TONE_CLASS.neutral)}>
      <HelpCircle aria-hidden="true" />
      {children ?? t('state.unknown')}
    </span>
  );
}
